// Experimental vector-search island (/labs/search, unlisted) — demo of what the
// SigLIP embedding space can do. Five tabs: Search (text/seed/filters + garnish),
// Vibe pad (live 2D anchor blending), Map (t-SNE catalog landscape), Upload
// (search by any image), Artists (style centroids).
import { useState } from "preact/hooks";
import { api } from "../../api";
import { IMG_ROOT } from "../../lib/constants";
import { Grid, type Item } from "./Grid";
import { VibePad } from "./VibePad";
import { CatalogMap } from "./CatalogMap";
import { UploadSearch } from "./UploadSearch";
import { ArtistMatch } from "./ArtistMatch";
import { SemanticMixer } from "./SemanticMixer";

// The island payload contract lives in packages/shared so the server that emits
// it and this component compile against one declaration. Re-exported here
// because the existing importers reference it from this module.
import type { LabsSearchInit } from "@brave-new-world/shared";
export type { LabsSearchInit };

type Tab = "search" | "pad" | "map" | "upload" | "artists";

interface QueryState {
  q: string;
  negative: string;
  seedId: number | null;
  seedPath: string;
  seedIds: number[];
  blend: number;
  colors: string[];
  categoryId: number;
  orientation: "" | "horizontal" | "vertical" | "square";
  gated: boolean;
}

const INITIAL: QueryState = {
  q: "",
  negative: "",
  seedId: null,
  seedPath: "",
  seedIds: [],
  blend: 0.5,
  colors: [],
  categoryId: 0,
  orientation: "",
  gated: true,
};

const PRESETS = [
  "golden hour",
  "scandi minimal interior",
  "moody forest",
  "pastel abstract",
  "brutalist architecture",
  "cozy reading nook",
];

const TABS: { key: Tab; label: string }[] = [
  { key: "search", label: "Search" },
  { key: "pad", label: "Vibe pad" },
  { key: "map", label: "Map" },
  { key: "upload", label: "Upload" },
  { key: "artists", label: "Artists" },
];

const CSS = `
#labs-search-root .ls-wrap { max-width: 1200px; margin: 0 auto; padding: 16px; }
#labs-search-root .ls-row { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 10px; }
#labs-search-root input[type="text"] { flex: 1 1 280px; max-width: 480px; padding: 8px 10px; font-size: 15px; }
#labs-search-root .ls-tabs { display: flex; gap: 4px; border-bottom: 2px solid #ddd; margin-bottom: 14px; }
#labs-search-root .ls-tab { padding: 8px 16px; cursor: pointer; border-radius: 6px 6px 0 0; font-weight: 600; }
#labs-search-root .ls-tab.on { background: #222; color: #fff; }
#labs-search-root .ls-chip { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px;
  border: 1px solid #ccc; border-radius: 14px; cursor: pointer; user-select: none; font-size: 13px; }
#labs-search-root .ls-chip.on { border-color: #333; box-shadow: 0 0 0 1px #333 inset; }
#labs-search-root .ls-swatch { width: 14px; height: 14px; border-radius: 50%; border: 1px solid #0002; }
#labs-search-root .ls-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 8px; }
#labs-search-root .ls-cell { position: relative; cursor: pointer; }
#labs-search-root .ls-cell img { width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 4px; display: block; }
#labs-search-root .ls-dist { position: absolute; bottom: 4px; left: 6px; font-size: 11px;
  background: rgba(0,0,0,0.6); color: #fff; padding: 1px 5px; border-radius: 3px; }
#labs-search-root .ls-dupe { position: absolute; top: 4px; right: 4px; font-size: 10px; font-weight: 700;
  background: #d02424; color: #fff; padding: 1px 5px; border-radius: 3px; }
#labs-search-root .ls-seed { display: inline-flex; align-items: center; gap: 8px; padding: 4px 8px;
  border: 1px dashed #888; border-radius: 6px; }
#labs-search-root .ls-seed img { width: 36px; height: 36px; object-fit: cover; border-radius: 4px; }
#labs-search-root .ls-meta { color: #888; font-size: 13px; margin: 6px 0; }
#labs-search-root .ls-trail { display: flex; gap: 4px; align-items: center; overflow-x: auto; }
#labs-search-root .ls-trail img { width: 32px; height: 32px; object-fit: cover; border-radius: 4px;
  cursor: pointer; opacity: 0.75; }
#labs-search-root .ls-trail img:hover { opacity: 1; }
#labs-search-root .ls-tray { display: inline-flex; gap: 4px; align-items: center; padding: 4px 8px;
  border: 1px solid #f2a541; border-radius: 6px; }
#labs-search-root .ls-tray img { width: 32px; height: 32px; object-fit: cover; border-radius: 4px; cursor: pointer; }
#labs-search-root .ls-pad { position: relative; width: 260px; height: 260px; flex: 0 0 auto;
  border: 1px solid #999; border-radius: 8px; background: linear-gradient(135deg, #f6f6f6, #e7e7ee);
  touch-action: none; cursor: crosshair; }
#labs-search-root .ls-pad-label { position: absolute; font-size: 11px; color: #666; pointer-events: none; }
#labs-search-root .ls-pad-dot { position: absolute; width: 16px; height: 16px; border-radius: 50%;
  background: #d02424; border: 2px solid #fff; box-shadow: 0 1px 4px #0006;
  transform: translate(-50%, -50%); pointer-events: none; }
#labs-search-root .ls-map { position: relative; width: 100%; height: 68vh; border: 1px solid #ccc;
  border-radius: 8px; overflow: hidden; background: #16161c; touch-action: none; }
#labs-search-root .ls-map-tip { position: absolute; pointer-events: none; background: #000d; color: #eee;
  padding: 6px; border-radius: 6px; font-size: 12px; max-width: 180px; z-index: 3; }
#labs-search-root .ls-map-tip img { width: 160px; height: 110px; object-fit: cover; border-radius: 4px; display: block; margin-bottom: 4px; }
#labs-search-root .ls-drop { display: flex; align-items: center; justify-content: center; width: 260px;
  height: 180px; border: 2px dashed #999; border-radius: 8px; cursor: pointer; color: #888;
  font-size: 13px; text-align: center; overflow: hidden; flex: 0 0 auto; }
#labs-search-root .ls-drop img { width: 100%; height: 100%; object-fit: contain; }
#labs-search-root .ls-artist { border: 1px solid #ddd; border-radius: 8px; padding: 10px; margin-bottom: 10px; }
#labs-search-root .ls-artist-head { display: flex; gap: 12px; align-items: baseline; margin-bottom: 6px; }
#labs-search-root .ls-artist-samples { display: flex; gap: 6px; }
#labs-search-root .ls-artist-samples img { width: 110px; height: 110px; object-fit: cover;
  border-radius: 4px; cursor: pointer; }
#labs-search-root .ls-mixer { border: 1px solid #e2e2e2; border-radius: 8px; padding: 8px 10px;
  margin-bottom: 10px; }
#labs-search-root .ls-mixer .ls-row { margin-bottom: 6px; }
#labs-search-root .ls-axis { display: flex; gap: 8px; align-items: center; margin-bottom: 4px; }
#labs-search-root .ls-axis input[type="text"] { flex: 0 1 160px; min-width: 90px; padding: 4px 8px; font-size: 13px; }
#labs-search-root .ls-axis input[type="range"] { flex: 1 1 220px; }
#labs-search-root .ls-axis-t { font-size: 12px; color: #888; width: 42px; text-align: right; }
`;

export function LabsSearchApp({ colors, categories }: LabsSearchInit) {
  const [tab, setTab] = useState<Tab>("search");
  const [qs, setQs] = useState<QueryState>(INITIAL);
  const [items, setItems] = useState<Item[] | null>(null);
  const [tookMs, setTookMs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [trail, setTrail] = useState<Item[]>([]);
  const [tray, setTray] = useState<Item[]>([]);
  const [artistSeed, setArtistSeed] = useState<number | null>(null);
  const [baseVec, setBaseVec] = useState<number[] | null>(null);
  const [tuned, setTuned] = useState(false);

  async function search(next: Partial<QueryState> = {}) {
    const s = { ...qs, ...next };
    setQs(s);
    setTuned(false);
    if (!s.q.trim() && !s.seedId && s.seedIds.length === 0) {
      setItems(null);
      setBaseVec(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.search.vector({
        q: s.q.trim() || undefined,
        negative: s.negative.trim() || undefined,
        seedId: s.seedIds.length > 0 ? undefined : (s.seedId ?? undefined),
        seedIds: s.seedIds.length > 0 ? s.seedIds : undefined,
        blend: s.blend,
        colors: s.colors,
        categoryId: s.categoryId || undefined,
        orientation: s.orientation || undefined,
        gated: s.gated,
        k: 48,
        returnVec: true, // base vector for the semantic mixer
      });
      setItems(res.items);
      setTookMs(res.tookMs);
      setBaseVec(res.queryVec ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  /** Mixer refinement: composed vector + the current filters (no q/seed). */
  async function tuneSearch(vec: number[]) {
    setLoading(true);
    setError("");
    try {
      const res = await api.search.vector({
        vec,
        colors: qs.colors,
        categoryId: qs.categoryId || undefined,
        orientation: qs.orientation || undefined,
        gated: qs.gated,
        k: 48,
      });
      setItems(res.items);
      setTookMs(res.tookMs);
      setTuned(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  function pushTrail(item: Item) {
    setTrail((t) => [item, ...t.filter((x) => x.id !== item.id)].slice(0, 12));
  }

  /** Seed a search from any tab (grid click, map dot, artist sample). */
  function seedFrom(item: Item) {
    pushTrail(item);
    setTab("search");
    void search({ seedId: item.id, seedPath: item.lowResPath, seedIds: [] });
  }

  function toggleTray(item: Item) {
    setTray((t) =>
      t.some((x) => x.id === item.id)
        ? t.filter((x) => x.id !== item.id)
        : t.length >= 8
          ? t
          : [...t, item]
    );
  }

  const onCell = (it: Item, shift: boolean) => {
    if (shift) toggleTray(it);
    else seedFrom(it);
  };

  const toggleColor = (tag: string) => {
    const on = qs.colors.includes(tag);
    void search({ colors: on ? qs.colors.filter((t) => t !== tag) : [...qs.colors, tag] });
  };

  const searchTab = (
    <div>
      <form
        class="ls-row"
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
      >
        <input
          type="text"
          placeholder="misty forest at dawn, teal abstract calm, cozy scandinavian kitchen…"
          value={qs.q}
          onInput={(e) => setQs({ ...qs, q: (e.target as HTMLInputElement).value })}
        />
        <button type="submit" disabled={loading}>
          {loading ? "Searching…" : "Search"}
        </button>
        <span
          class="ls-chip"
          onClick={() => setAdvanced(!advanced)}
          title="negative terms + seed/text blend"
        >
          {advanced ? "− advanced" : "+ advanced"}
        </span>
      </form>

      <div class="ls-row">
        {PRESETS.map((p) => (
          <span
            key={p}
            class="ls-chip"
            onClick={() => void search({ q: p, seedId: null, seedPath: "", seedIds: [] })}
          >
            {p}
          </span>
        ))}
      </div>

      {advanced && (
        <div class="ls-row">
          <input
            type="text"
            placeholder="negative terms (steer away from, e.g. people)"
            value={qs.negative}
            onInput={(e) => setQs({ ...qs, negative: (e.target as HTMLInputElement).value })}
          />
          {(qs.seedId !== null || qs.seedIds.length > 0) && qs.q.trim() !== "" && (
            <label class="ls-chip" title="0 = all image, 1 = all text">
              image ↔ text
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={qs.blend}
                onChange={(e) =>
                  void search({ blend: Number((e.target as HTMLInputElement).value) })
                }
              />
            </label>
          )}
        </div>
      )}

      <div class="ls-row">
        {colors.map((c) => (
          <span
            key={c.tag}
            class={`ls-chip ${qs.colors.includes(c.tag) ? "on" : ""}`}
            onClick={() => toggleColor(c.tag)}
          >
            <span class="ls-swatch" style={`background:#${c.hex}`}></span>
            {c.name}
          </span>
        ))}
      </div>

      <div class="ls-row">
        <select
          value={qs.categoryId}
          onChange={(e) =>
            void search({ categoryId: Number((e.target as HTMLSelectElement).value) })
          }
        >
          <option value="0">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          value={qs.orientation}
          onChange={(e) =>
            void search({
              orientation: (e.target as HTMLSelectElement).value as QueryState["orientation"],
            })
          }
        >
          <option value="">Any orientation</option>
          <option value="horizontal">Horizontal</option>
          <option value="vertical">Vertical</option>
          <option value="square">Square</option>
        </select>
        <label class="ls-chip">
          <input
            type="checkbox"
            checked={qs.gated}
            onChange={(e) => void search({ gated: (e.target as HTMLInputElement).checked })}
          />
          sale-ready only
        </label>
        {qs.seedId !== null && qs.seedIds.length === 0 && (
          <span class="ls-seed">
            <img src={`${IMG_ROOT}${qs.seedPath}`} alt="" />
            more like #{qs.seedId}
            <span
              class="ls-chip"
              onClick={() => {
                setArtistSeed(qs.seedId);
                setTab("artists");
              }}
            >
              → similar artists
            </span>
            <span class="ls-chip" onClick={() => void search({ seedId: null, seedPath: "" })}>
              ✕
            </span>
          </span>
        )}
        {tray.length > 0 && (
          <span class="ls-tray">
            {tray.map((t) => (
              <img
                key={t.id}
                src={`${IMG_ROOT}${t.lowResPath}`}
                title="click to remove from tray"
                onClick={() => toggleTray(t)}
              />
            ))}
            <span
              class="ls-chip"
              onClick={() =>
                void search({ seedIds: tray.map((t) => t.id), seedId: null, seedPath: "" })
              }
            >
              more like these ({tray.length})
            </span>
            <span class="ls-chip" onClick={() => setTray([])}>
              ✕
            </span>
          </span>
        )}
      </div>

      <SemanticMixer baseVec={baseVec} onTune={tuneSearch} onClear={() => void search({})} />

      {trail.length > 0 && (
        <div class="ls-row">
          <span class="ls-meta">trail:</span>
          <span class="ls-trail">
            {trail.map((t) => (
              <img
                key={t.id}
                src={`${IMG_ROOT}${t.lowResPath}`}
                title={t.title}
                onClick={() => seedFrom(t)}
              />
            ))}
          </span>
        </div>
      )}

      {error && <p class="ls-meta">⚠ {error}</p>}
      {loading && qs.q.trim() !== "" && (
        <p class="ls-meta">
          First text search after idle can take up to a minute (the encoder cold-starts) — after
          that it's fast.
        </p>
      )}
      {items !== null && !loading && (
        <p class="ls-meta">
          {items.length} results in {tookMs} ms — click: more like this · shift-click: add to tray
        </p>
      )}
      {items !== null && (
        <Grid
          items={items}
          onCell={onCell}
          dupeThreshold={
            (qs.seedId !== null || qs.seedIds.length > 0) && !qs.q.trim() && !tuned
              ? 0.06
              : undefined
          }
        />
      )}
    </div>
  );

  return (
    <div class="ls-wrap">
      <style>{CSS}</style>
      <h1>Search lab</h1>
      <div class="ls-tabs">
        {TABS.map((t) => (
          <span
            key={t.key}
            class={`ls-tab ${tab === t.key ? "on" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </span>
        ))}
      </div>
      {tab === "search" && searchTab}
      {tab === "pad" && <VibePad gated={qs.gated} onSeed={seedFrom} />}
      {tab === "map" && <CatalogMap onSeed={seedFrom} />}
      {tab === "upload" && <UploadSearch gated={qs.gated} onSeed={seedFrom} />}
      {tab === "artists" && <ArtistMatch seedArtworkId={artistSeed} onSeed={seedFrom} />}
    </div>
  );
}
